import { useCallback, useEffect, useRef, useState } from "react";
import { applyAffine, solveAffine } from "../lib/gazeMath";

/**
 * useGazeTracker — opt-in webcam-based gaze tracker for Mode B.
 *
 * Loads @mediapipe/tasks-vision from CDN on demand (lazy: only when the user
 * opts in), grabs the user's webcam, runs FaceLandmarker at ~10 Hz, derives
 * a normalized iris-center per frame, and (after a 5-point calibration)
 * maps that to predicted screen coordinates.
 *
 * Privacy contract:
 *   - No video frames or landmarks ever leave the browser.
 *   - We expose only aggregate metrics + an in-memory per-frame log for the
 *     parent to summarize and POST to the backend.
 *
 * API (returned object):
 *   status      "idle" | "loading" | "ready" | "calibrating" | "tracking" | "error" | "denied"
 *   error       error message string when status === "error"
 *   videoRef    attach to a <video> element for the preview
 *   start()     request camera, load model
 *   stop()      release everything
 *   beginCalibration(points: [[sx,sy], ...]) — start 5-point sequence
 *   recordCalibrationPoint(index)  — sample current gaze for point #index
 *   finalizeCalibration()          — fit the affine
 *   getLatestGaze()                — { x, y, t, confidence } | null
 *   metrics                        — { gazeOnDotPct, trackingLagMs, stabilityIndex, dropoutCount, sampleCount }
 *   tickAgainstDot(dotX, dotY, hitRadiusPx) — call each frame to log + score
 */
const MEDIAPIPE_BASE =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14";
const WASM_PATH = `${MEDIAPIPE_BASE}/wasm`;
const MODEL_PATH =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

export default function useGazeTracker() {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({
    gazeOnDotPct: 0,
    trackingLagMs: 0,
    stabilityIndex: 0,
    dropoutCount: 0,
    sampleCount: 0,
  });

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const lastInferRef = useRef(0);
  const lastGazeRef = useRef(null); // { x, y, t, confidence }
  const lastDotSeenRef = useRef(null); // last dot position the parent told us about + when gaze first landed on it
  const calibSamplesRef = useRef([]); // [{idx, irisX, irisY}]
  const calibTargetsRef = useRef([]); // [[sx, sy]]
  const affineRef = useRef(null);
  const gazeLogRef = useRef([]); // [{t, ix, iy, sx, sy, dotX, dotY, onDot, lagMs}]
  const dropoutRef = useRef({ lastSampleT: 0, inDropout: false });

  // ---------------- model + camera bootstrap ----------------
  const start = useCallback(async () => {
    if (status === "loading" || status === "ready" || status === "tracking") return;
    setStatus("loading");
    setError(null);
    try {
      // 1. lazy-load mediapipe ESM from CDN
      const vision = await import(/* webpackIgnore: true */ `${MEDIAPIPE_BASE}/vision_bundle.mjs`);
      const fileset = await vision.FilesetResolver.forVisionTasks(WASM_PATH);
      const landmarker = await vision.FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_PATH, delegate: "GPU" },
        runningMode: "VIDEO",
        numFaces: 1,
        outputFaceBlendshapes: false,
        outputFacialTransformationMatrixes: false,
      });
      landmarkerRef.current = landmarker;

      // 2. webcam (user-facing, low-res — enough for iris)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setStatus("ready");
      scheduleLoop();
    } catch (e) {
      // distinguish a permission denial from other failures
      const msg = String(e?.message || e || "Gaze tracker failed to start");
      if (/Permission|denied|NotAllowed/i.test(msg)) {
        setStatus("denied");
      } else {
        setStatus("error");
      }
      setError(msg);
    }
  }, [status]);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (landmarkerRef.current) {
      try {
        landmarkerRef.current.close();
      } catch (err) { console.debug("gaze landmarker close:", err); }
      landmarkerRef.current = null;
    }
    setStatus("idle");
  }, []);

  useEffect(() => () => stop(), [stop]);

  // ---------------- inference loop (10 Hz) ----------------
  const scheduleLoop = useCallback(() => {
    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      const now = performance.now();
      if (now - lastInferRef.current < 100) return; // 10 Hz
      lastInferRef.current = now;
      const video = videoRef.current;
      const lm = landmarkerRef.current;
      if (!video || !lm || video.readyState < 2) return;
      try {
        const res = lm.detectForVideo(video, now);
        if (!res.faceLandmarks || !res.faceLandmarks[0]) {
          handleDropout(now);
          return;
        }
        const pts = res.faceLandmarks[0];
        // MediaPipe FaceLandmarker returns 478 normalized landmarks when iris
        // refinement is on. Iris indices: 468..472 (left eye), 473..477 (right).
        const leftIris = avg(pts, [468, 469, 470, 471, 472]);
        const rightIris = avg(pts, [473, 474, 475, 476, 477]);
        const irisCx = (leftIris.x + rightIris.x) / 2;
        const irisCy = (leftIris.y + rightIris.y) / 2;
        lastGazeRef.current = {
          ix: irisCx,
          iy: irisCy,
          t: now,
          confidence: 1.0,
        };
        dropoutRef.current.inDropout = false;
        dropoutRef.current.lastSampleT = now;
      } catch (_e) {
        handleDropout(now);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const handleDropout = (now) => {
    const d = dropoutRef.current;
    if (!d.inDropout && now - d.lastSampleT > 2000) {
      d.inDropout = true;
      gazeLogRef.current.push({ t: now, dropout: true });
    }
  };

  // ---------------- calibration ----------------
  const beginCalibration = useCallback((targets) => {
    calibSamplesRef.current = [];
    calibTargetsRef.current = targets;
    affineRef.current = null;
    setStatus("calibrating");
  }, []);

  const recordCalibrationPoint = useCallback((idx) => {
    // Take the average of the last ~400 ms of iris samples for stability.
    const g = lastGazeRef.current;
    if (!g) return false;
    calibSamplesRef.current.push({ idx, ix: g.ix, iy: g.iy });
    return true;
  }, []);

  const finalizeCalibration = useCallback(() => {
    const samples = calibSamplesRef.current;
    const targets = calibTargetsRef.current;
    if (samples.length < 3) {
      setStatus("error");
      setError("Need at least 3 calibration points");
      return false;
    }
    try {
      const src = samples.map((s) => [s.ix, s.iy]);
      const dst = samples.map((s) => targets[s.idx]);
      affineRef.current = solveAffine(src, dst);
      setStatus("tracking");
      return true;
    } catch (e) {
      setStatus("error");
      setError("Calibration solve failed — try again in better lighting.");
      return false;
    }
  }, []);

  // ---------------- per-frame scoring (called by Mode B's RAF loop) ----------------
  const tickAgainstDot = useCallback((dotX, dotY, hitRadiusPx) => {
    const g = lastGazeRef.current;
    const M = affineRef.current;
    if (!g || !M) return;
    const [sx, sy] = applyAffine(M, g.ix, g.iy);
    const dist = Math.hypot(sx - dotX, sy - dotY);
    const onDot = dist <= hitRadiusPx;
    let lagMs = 0;
    const lastSeen = lastDotSeenRef.current;
    if (lastSeen && lastSeen.dotX === dotX && lastSeen.dotY === dotY) {
      if (onDot && !lastSeen.landed) {
        lagMs = performance.now() - lastSeen.t;
        lastDotSeenRef.current = { ...lastSeen, landed: true, lagMs };
      }
    } else {
      lastDotSeenRef.current = { dotX, dotY, t: performance.now(), landed: onDot, lagMs: 0 };
    }
    gazeLogRef.current.push({
      t: g.t,
      ix: g.ix,
      iy: g.iy,
      sx,
      sy,
      dotX,
      dotY,
      onDot,
      lagMs,
    });
  }, []);

  // ---------------- aggregate metrics, updated every ~500 ms ----------------
  useEffect(() => {
    if (status !== "tracking") return;
    const id = setInterval(() => {
      const log = gazeLogRef.current.filter((e) => !e.dropout);
      const drops = gazeLogRef.current.filter((e) => e.dropout).length;
      if (log.length === 0) return;
      const onDot = log.filter((e) => e.onDot).length;
      const gazeOnDotPct = onDot / log.length;
      const lags = log.map((e) => e.lagMs).filter((m) => m > 0);
      const trackingLagMs = lags.length
        ? lags.reduce((a, b) => a + b, 0) / lags.length
        : 0;
      // stability: 1 - normalized stddev of (sx, sy) during on-dot frames
      const fixated = log.filter((e) => e.onDot);
      let stability = 0;
      if (fixated.length > 5) {
        const mx = fixated.reduce((a, e) => a + e.sx, 0) / fixated.length;
        const my = fixated.reduce((a, e) => a + e.sy, 0) / fixated.length;
        const variance =
          fixated.reduce((a, e) => a + (e.sx - mx) ** 2 + (e.sy - my) ** 2, 0) /
          fixated.length;
        const std = Math.sqrt(variance);
        // normalize against viewport diagonal as a generous denominator
        const diag = Math.hypot(window.innerWidth, window.innerHeight) || 1;
        stability = Math.max(0, Math.min(1, 1 - std / (diag * 0.2)));
      }
      setMetrics({
        gazeOnDotPct: Number(gazeOnDotPct.toFixed(3)),
        trackingLagMs: Math.round(trackingLagMs),
        stabilityIndex: Number(stability.toFixed(3)),
        dropoutCount: drops,
        sampleCount: log.length,
      });
    }, 500);
    return () => clearInterval(id);
  }, [status]);

  const getLatestGaze = useCallback(() => lastGazeRef.current, []);
  const getFinalMetrics = useCallback(() => metrics, [metrics]);

  return {
    status,
    error,
    videoRef,
    start,
    stop,
    beginCalibration,
    recordCalibrationPoint,
    finalizeCalibration,
    getLatestGaze,
    tickAgainstDot,
    metrics,
    getFinalMetrics,
  };
}

function avg(pts, indices) {
  let x = 0,
    y = 0;
  for (const i of indices) {
    x += pts[i].x;
    y += pts[i].y;
  }
  return { x: x / indices.length, y: y / indices.length };
}
